import { XMLParser, XMLValidator } from "fast-xml-parser";
import { z } from "zod";

import {
  classifyEcwValidationFailure,
  ecwResponseErrorMap,
  EcwResponseParseReason,
  EcwResponseParseStage,
  safeEcwResponseKey,
  summarizeEcwValidationIssues,
  type EcwResponseShape,
  type EcwValidationIssue,
} from "@/src/lib/ehr/ecw/webecw/errors";

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: "@_",
  parseAttributeValue: false,
  parseTagValue: false,
  trimValues: true,
  allowBooleanAttributes: true,
  isArray: (tagName) => {
    return [
      "patient",
      "doctor",
      "insurance",
      "FIELDS",
      "row",
      "encounter",
      "AdditionalNote",
      "coded",
      "uncoded",
      "problem",
    ].includes(tagName);
  },
  removeNSPrefix: true,
});

const MAX_SHAPE_KEYS = 10;

function responseValueKind(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";

  return typeof value;
}

function responseObject(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function responseKeys(value: unknown): string[] {
  const object = responseObject(value);

  if (!object) return [];

  return Object.keys(object).sort().slice(0, MAX_SHAPE_KEYS).map(safeEcwResponseKey);
}

function summarizeSoapResponseShape(parsed: unknown): EcwResponseShape {
  const root = responseObject(parsed);
  const envelope = responseObject(root?.Envelope);
  const body = responseObject(envelope?.Body);
  const returned = body?.return;
  const rootKeys = responseKeys(root);
  const envelopeKeys = responseKeys(envelope);
  const bodyKeys = responseKeys(body);
  const returnKeys = responseKeys(returned);
  const returnKind = responseValueKind(returned);

  return {
    bodyKeys,
    envelopeKeys,
    returnKeys,
    returnKind,
    rootKeys,
    structuralFingerprint: [
      `root[${rootKeys.join(",")}]`,
      `envelope[${envelopeKeys.join(",")}]`,
      `body[${bodyKeys.join(",")}]`,
      `return:${returnKind}[${returnKeys.join(",")}]`,
    ].join("|"),
  };
}

export class EcwSoapParseError extends Error {
  constructor(
    public readonly parseStage: EcwResponseParseStage,
    public readonly parseReason: EcwResponseParseReason,
    public readonly validationIssues: EcwValidationIssue[],
    public readonly responseShape?: EcwResponseShape
  ) {
    super(`eCW SOAP response failed during ${parseStage}`);
    this.name = "EcwSoapParseError";
  }
}

export function parseSoapEnvelope<S extends z.ZodTypeAny>(xml: string, schema: S): z.infer<S> {
  let parsed: unknown;

  try {
    const validationResult = XMLValidator.validate(xml, { allowBooleanAttributes: true });

    if (validationResult !== true) {
      throw new Error("Invalid XML");
    }

    parsed = parser.parse(xml);
  } catch {
    throw new EcwSoapParseError(EcwResponseParseStage.XML_DECODE, EcwResponseParseReason.MALFORMED_XML, []);
  }

  const responseShape = summarizeSoapResponseShape(parsed);
  const root = responseObject(parsed);
  const envelope = responseObject(root?.Envelope);
  const body = responseObject(envelope?.Body);

  if (!root || !("Envelope" in root)) {
    throw new EcwSoapParseError(
      EcwResponseParseStage.SOAP_ENVELOPE,
      EcwResponseParseReason.MISSING_SOAP_ENVELOPE,
      [],
      responseShape
    );
  }

  if (!envelope || !("Body" in envelope)) {
    throw new EcwSoapParseError(
      EcwResponseParseStage.SOAP_ENVELOPE,
      EcwResponseParseReason.MISSING_SOAP_BODY,
      [],
      responseShape
    );
  }

  if (!body || !("return" in body)) {
    throw new EcwSoapParseError(
      EcwResponseParseStage.SOAP_ENVELOPE,
      EcwResponseParseReason.MISSING_SOAP_RETURN,
      [],
      responseShape
    );
  }

  const result = schema.safeParse(body.return, { error: ecwResponseErrorMap });

  if (!result.success) {
    throw new EcwSoapParseError(
      EcwResponseParseStage.RESPONSE_SCHEMA,
      classifyEcwValidationFailure(result.error),
      summarizeEcwValidationIssues(result.error),
      responseShape
    );
  }

  return result.data;
}

export function asArray<T>(v: T | T[] | undefined | null): T[] {
  if (v === undefined || v === null) return [];

  return Array.isArray(v) ? v : [v];
}

export function xmlArray<S extends z.ZodTypeAny>(item: S) {
  return z.preprocess(asArray, z.array(item));
}

export function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export enum SoapXsdType {
  STRING = "string",
  INT = "int",
}

export interface SoapEnvelopeSection {
  wrapper: string;
  fields: Record<string, string>;
  cdataKeys?: readonly string[];
  fieldTypes?: Readonly<Record<string, SoapXsdType | undefined>>;
}

function buildSoapSection({ wrapper, fields, cdataKeys = [], fieldTypes = {} }: SoapEnvelopeSection): string {
  const elements = Object.entries(fields)
    .map(([key, value]) => {
      const fieldType = fieldTypes[key] ?? SoapXsdType.STRING;

      return cdataKeys.includes(key)
        ? `<${key} xsi:type="xsd:${fieldType}"><![CDATA[${value.replace(/]]>/g, "]]]]><![CDATA[>")}]]></${key}>`
        : `<${key} xsi:type="xsd:${fieldType}">${escapeXml(value)}</${key}>`;
    })
    .join("");

  return `<${wrapper}>${elements}</${wrapper}>`;
}

export function buildSoapEnvelope(
  wrapper: string,
  fields: Record<string, string>,
  cdataKeys: readonly string[] = [],
  fieldTypes: Readonly<Record<string, SoapXsdType | undefined>> = {}
): string {
  return buildSoapEnvelopeSections([{ wrapper, fields, cdataKeys, fieldTypes }]);
}

/** Builds sibling SOAP payload sections for compound eCW writes. */
export function buildSoapEnvelopeSections(sections: readonly SoapEnvelopeSection[]): string {
  const body = sections.map(buildSoapSection).join("");

  return (
    '<S:Envelope xmlns:S="http://schemas.xmlsoap.org/soap/envelope/" ' +
    'xmlns:SOAP-ENC="http://schemas.xmlsoap.org/soap/encoding/" ' +
    'S:encodingStyle="http://schemas.xmlsoap.org/soap/encoding/" ' +
    'xmlns:xsd="http://www.w3.org/1999/XMLSchema" ' +
    'xmlns:xsi="http://www.w3.org/1999/XMLSchema-instance">' +
    "<Body>" +
    '<m:NOOP xmlns:m="NOOP">' +
    body +
    "</m:NOOP>" +
    "</Body>" +
    "</S:Envelope>"
  );
}
